import { ArrowRight, ArrowUpRight, Medal, Mountain, Users } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

const Home: React.FC = () => (
  <>
    <section className="hero-grid overflow-hidden bg-[#2d174d] text-white">
      <div className="site-shell grid min-h-[720px] items-center gap-12 py-20 lg:grid-cols-[1.08fr_0.92fr]">
        <div className="relative z-10">
          <p className="eyebrow text-emerald-300">Washington ARML</p>
          <h1 className="mt-5 max-w-4xl text-6xl font-black leading-[0.93] tracking-[-0.055em] sm:text-7xl lg:text-[5.8rem]">
            Washington placed third at ARML{" "}
            <span className="block text-emerald-300">in 2025 and 2026.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-xl leading-8 text-slate-300">
            Washington A1 placed third at ARML in 2025, and Washington A4 placed
            third again in 2026.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              className="inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 font-extrabold text-slate-950 transition hover:bg-emerald-300"
              to="/arml-results"
            >
              Explore the official results
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
            <a
              className="inline-flex items-center gap-2 rounded-full border border-violet-300/40 bg-violet-950/40 px-6 py-3 font-bold text-white transition hover:border-violet-200"
              href="#about"
            >
              Read how WARML works
              <ArrowRight size={18} aria-hidden="true" />
            </a>
          </div>
        </div>

        <div
          className="relative mx-auto aspect-square w-full max-w-[34rem]"
          aria-label="Washington's third-place ARML finishes in 2026 and 2025"
        >
          <div className="absolute inset-[7%] rounded-full border border-sky-300/20" />
          <div className="absolute inset-[17%] rounded-full border border-violet-300/20" />
          <div className="absolute left-[4%] top-[16%] w-[58%] -rotate-6 rounded-[2rem] border border-emerald-300/30 bg-[#173f32] p-6 shadow-2xl sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <p className="eyebrow text-emerald-200">ARML 2026</p>
              <img
                className="w-24 rounded-2xl border border-emerald-200/30 shadow-lg"
                src="/washington-arml-2026.png"
                alt="Washington ARML 2026 team shirt mark with the Space Needle"
              />
            </div>
            <p className="mt-4 text-7xl font-black tracking-tight text-white">
              3rd
            </p>
            <p className="mt-2 text-xl font-bold text-slate-200">
              Washington A4
            </p>
            <p className="mt-5 border-t border-slate-700 pt-5 text-sm leading-6 text-slate-400">
              213 points · Reno
            </p>
          </div>
          <div className="absolute bottom-[7%] right-[1%] w-[58%] rotate-5 rounded-[2rem] bg-blue-100 p-6 text-blue-950 shadow-2xl sm:p-8">
            <p className="eyebrow text-blue-800">ARML 2025</p>
            <p className="mt-4 text-7xl font-black tracking-tight">3rd</p>
            <p className="mt-2 text-xl font-bold">Washington A1</p>
            <p className="mt-5 border-t border-blue-900/20 pt-5 text-sm leading-6 text-blue-950/75">
              228 points · Reno
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="border-y border-slate-200 bg-white">
      <div className="site-shell grid md:grid-cols-3">
        <div className="achievement-stat">
          <span className="achievement-number">2</span>
          <span className="achievement-label">
            consecutive third-place ARML finishes
          </span>
        </div>
        <div className="achievement-stat">
          <span className="achievement-number">10</span>
          <span className="achievement-label">
            Washington individual honors across the two seasons
          </span>
        </div>
        <div className="achievement-stat">
          <span className="achievement-number">1st</span>
          <span className="achievement-label">
            Washington Gold at February HMMT 2026
          </span>
        </div>
      </div>
    </section>

    <section className="site-shell py-24" id="about">
      <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr]">
        <div>
          <p className="eyebrow text-[#4b2e83]">A program with depth</p>
          <h2 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
            Washington placed third at ARML in 2025 and again in 2026.
          </h2>
          <p className="mt-6 text-lg leading-8 text-slate-700">
            WARML brings students together across schools and grade levels to
            solve hard problems and explain their reasoning to each other.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <article className="story-card bg-emerald-100">
            <div className="story-icon bg-emerald-300">
              <Medal aria-hidden="true" />
            </div>
            <p className="eyebrow text-emerald-900">June 2026</p>
            <h3 className="mt-3 text-2xl font-black">
              Washington A4 placed third.
            </h3>
            <p className="mt-4 leading-7 text-slate-700">
              Washington A4 scored 213 points and placed third at ARML. Ian Rui
              and Alexander Yang each answered nine of ten individual problems,
              tied for second in the country, and five more Washington students
              answered eight.
            </p>
            <Link className="story-link" to="/arml-results">
              Read the 2026 result <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </article>

          <article className="story-card bg-blue-100">
            <div className="story-icon bg-blue-300">
              <Mountain aria-hidden="true" />
            </div>
            <p className="eyebrow text-blue-900">May 2025</p>
            <h3 className="mt-3 text-2xl font-black">
              Washington A1 placed third.
            </h3>
            <p className="mt-4 leading-7 text-slate-700">
              Washington A1 scored 228 points at Reno, WARML’s first time in the
              nation’s top three. Aryan Agrawal, Ian Rui, and Rick Zhou each
              answered every individual problem correctly, and Aryan finished
              11th in the national tiebreaker.
            </p>
            <Link className="story-link" to="/arml-results">
              Read both seasons’ results{" "}
              <ArrowRight size={17} aria-hidden="true" />
            </Link>
          </article>

          <article className="story-card bg-violet-100 sm:col-span-2 sm:grid sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-6">
            <div className="story-icon bg-violet-300">
              <Users aria-hidden="true" />
            </div>
            <div>
              <p className="eyebrow text-violet-900">February HMMT 2026</p>
              <h3 className="mt-3 text-2xl font-black">
                Washington Gold wins Team Sweepstakes
              </h3>
              <p className="mt-3 max-w-3xl leading-7 text-slate-700">
                Washington Gold 1—whose roster had deep roots among current and
                former WARML students—had the highest score in Guts, fifth place
                in the Team Round, and brought home first place in the
                Sweepstakes, the overall team championship. WARML captain
                Benjamin Fu was the overall top scorer in the Geometry Round.
              </p>
            </div>
            <a
              className="mt-6 inline-flex items-center gap-2 font-extrabold text-violet-950 sm:mt-0"
              href="https://hmmt-archive.s3.amazonaws.com/tournaments/2026/feb/results/short.htm"
              target="_blank"
              rel="noreferrer"
            >
              Official results
              <ArrowUpRight size={17} aria-hidden="true" />
            </a>
          </article>
        </div>
      </div>
    </section>

    <section className="bg-[#321b55] py-24 text-white">
      <div className="site-shell">
        <div className="max-w-5xl">
          <p className="eyebrow text-emerald-300">Inside a WARML practice</p>
          <h2 className="mt-4 text-4xl font-black tracking-tight sm:text-5xl">
            Students solve separately, argue about methods, and finish together.
          </h2>
          <div className="mt-8 grid gap-6 text-lg leading-8 text-violet-100 lg:grid-cols-2">
            <p>
              On an ordinary practice day, students try the same difficult set,
              compare approaches, and explain every step that does not survive
              scrutiny. Relay work makes that dependence literal: one runner’s
              answer becomes the next runner’s input.
            </p>
            <p>
              The power round asks fifteen students to produce one coherent
              proof under time pressure. Washington placed third in 2025 and
              third again in 2026, with much of the same group of students.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="site-shell py-24">
      <div className="rounded-[2rem] bg-white p-8 shadow-sm ring-1 ring-slate-200 sm:p-12 lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:gap-10">
        <div>
          <p className="eyebrow text-[#4b2e83]">
            How Washington students can join
          </p>
          <h2 className="mt-3 text-3xl font-black tracking-tight">
            Students do not need to know every trick before they join.
          </h2>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-700">
            WARML welcomes students who want to grow through challenging,
            collaborative mathematics. You do not need to arrive knowing every
            trick; you need to be ready to think, contribute, and keep going.
          </p>
        </div>
        <a
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4b2e83] px-6 py-3 font-extrabold text-white transition hover:bg-[#3a2367] lg:mt-0"
          href="mailto:armlwacoach@gmail.com"
        >
          Contact the WARML coaches
          <ArrowUpRight size={18} aria-hidden="true" />
        </a>
      </div>
    </section>
  </>
);

export default Home;
